/**
 * TIER-2 BOOST LIGHTNING — jagged white-hot cracks arcing between the
 * player's own limbs while boost tier 2 is active.
 *
 * Owner, 2026-08-14: *"can you add thunders on player when he is on boost 2?
 * dont underestimate the 3.js do your best prompt and creativity"*. Tier 2
 * already has embers rising off him (`boostAura.ts`) and a brief bloom flare
 * at their hot core (`GLOW_INTENSITY`) — thunder is a second, sharper layer
 * on the SAME tier, not a competing effect: it fades from white-hot down to
 * the exact dark red (`0x8f0000`) the embers themselves glow at, so the two
 * read as one power crackling, not two unrelated VFX sharing a body.
 *
 * ── WHY LINE SEGMENTS, NOT BILLBOARD QUADS ──────────────────────────────────
 *
 * `boostAura.ts` earns width for its embers because a particle is a blob —
 * a quad reads fine at any size. A lightning bolt is the opposite: real
 * lightning is razor-thin at the core and reads as "thick" only because it
 * blooms. WebGL line width is 1px on essentially every platform (ANGLE
 * clamps it), which sounds like a limitation but is actually the correct
 * asset for this effect — a thin, HDR-bright line is exactly what this
 * game's own bloom pass (`postfx.ts`, threshold 1.0) turns into a glowing
 * crack for free, the same trick `boostAura.ts` already uses for its ember
 * core, pushed further here (`GLOW_INTENSITY = 3.4`) because a bolt is
 * meant to be the brightest thing in the effect, if only for ~120ms.
 * `vfx.ts`'s rain already renders `LineSegments` this way in this exact
 * codebase — this is a proven pattern, not a novel risk.
 *
 * ── HOW A BOLT'S SHAPE IS BUILT: MIDPOINT DISPLACEMENT ──────────────────────
 *
 * Two of the SAME five body anchors `boostAura.ts` emits embers from (reused
 * via its exported `findEmberRig` — one rig, two effects, never two
 * disagreeing sets of points) are picked at random as the bolt's ends. The
 * path between them is the classic real-time-lightning fractal: start with
 * one straight segment, recursively split every segment at its midpoint and
 * displace the split point sideways by a random amount that halves each
 * recursion level (`midpointDisplace`). Three levels makes eight segments —
 * jagged enough to read as electricity, cheap enough that `MAX_BOLTS` of
 * them cost 54 vertices total.
 *
 * The displacement is computed ONCE at spawn, in a local (t, u, v) frame —
 * t along the anchor-to-anchor line, u/v on a perpendicular basis built once
 * from that line's direction. Every subsequent frame re-samples the anchor
 * bones' CURRENT world positions and reconstructs the same shape against
 * them (`boltPos`), so a bolt's zigzag holds still (a real strike does not
 * re-shuffle mid-flash) while its endpoints track the moving body. Given a
 * bolt lives ~100ms this is correct even though the basis itself is not
 * re-orthogonalised per frame — the body cannot rotate meaningfully in that
 * time.
 *
 * ── WHY IT READS AS ELECTRICITY, NOT A FLASHING LINE ────────────────────────
 *
 * Three things beyond the zigzag: (1) a fast-attack/fast-decay lifetime
 * envelope around 100-150ms — real lightning is a strike, not a fade; (2) a
 * per-bolt sine "flicker" multiplied into that envelope so brightness
 * stutters rather than ramps smoothly, the same multi-return-stroke cue real
 * footage has; (3) a ~45% chance of one short branch stub off an interior
 * point, because a single clean arc reads as a laser and a forked one reads
 * as a spark. `MAX_BOLTS = 3` concurrent, spawned on a randomised
 * 0.16-0.42s timer, means 1-2 are usually alive and overlapping — a crackle,
 * not a metronome.
 *
 * Tier 1 gets nothing here on purpose — this was asked for tier 2 only, and
 * an identical crackle on both tiers would erase the distinction `boostAura`
 * already worked to draw between them (dark red vs the brighter tier 1).
 *
 * Presentation only (§15.2): reads RenderState, writes nothing back.
 */
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  LineSegments,
  MathUtils,
  ShaderMaterial,
  NormalBlending,
  Vector3,
  type Object3D,
} from 'three';
import type { RenderContext, RenderState, RenderSubsystem } from './context.js';
import { findHandRig } from './handAttach.js';
import { findEmberRig, type EmberRig } from './boostAura.js';

/** Concurrent bolts, pooled globally like `boostAura.ts`'s particle cap.
 *  Owner, 2026-08-14: "increase them please" — doubled from 3, and paired
 *  with the shorter spawn interval below so several are usually overlapping
 *  rather than 1-2 at a time. */
const MAX_BOLTS = 6;
/** Recursion depth of the midpoint-displacement fractal. 3 -> 8 segments. */
const BOLT_LEVELS = 3;
const SEGMENTS_PER_BOLT = 1 << BOLT_LEVELS;
/** +1 branch segment budget per bolt (used only when `BRANCH_CHANCE` hits). */
const MAX_VERTS_PER_BOLT = SEGMENTS_PER_BOLT * 2 + 2;
const MAX_VERTS = MAX_BOLTS * MAX_VERTS_PER_BOLT;

/** Seconds between spawns while tier 2 is active — randomised so bolts never
 *  land on a visible metronome. Halved from [0.16, 0.42] for "increase them". */
const BOLT_INTERVAL_MIN = 0.08;
const BOLT_INTERVAL_MAX = 0.20;
/** Seconds a single bolt lives — a strike, not a fade; real lightning is
 *  gone within a couple of frames at 60fps. */
const BOLT_LIFE_MIN = 0.10;
const BOLT_LIFE_MAX = 0.16;
/** First-level sideways displacement, as a fraction of the anchor-to-anchor
 *  distance (halved every subsequent recursion level). */
const JITTER_FRACTION = 0.24;
/** Chance a bolt grows one short branch off an interior point. */
const BRANCH_CHANCE = 0.45;

/** White-hot at spawn, settling toward the EXACT dark red `boostAura.ts`'s
 *  tier-2 embers glow at (`CORE_COLOR[2]` there) — one palette, two effects. */
const BOLT_CORE_COLOR = 0xfff6ec;
const BOLT_EDGE_COLOR = 0x8f0000;
/** Brighter than the embers' own 2.2 — a bolt is meant to be the single
 *  brightest thing in the effect, if only for a frame or two. Crosses the
 *  postfx bloom threshold (1.0) hard, same mechanism as boostAura.ts. */
const GLOW_INTENSITY = 3.4;

const VERTEX_SHADER = /* glsl */ `
attribute vec3 aColor;
attribute float aAlpha;
varying vec3 vColor;
varying float vAlpha;

void main() {
  vColor = aColor;
  vAlpha = aAlpha;
  // Raw world-space vertices, rewritten every frame from the anchor bones —
  // no instancing, no billboard: a line has no "facing" to fight.
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT_SHADER = /* glsl */ `
varying vec3 vColor;
varying float vAlpha;

void main() {
  if (vAlpha <= 0.003) discard;
  gl_FragColor = vec4(vColor, vAlpha);
}
`;

/** One point on a bolt's path, in the LOCAL frame built at spawn: `t` along
 *  the anchor-to-anchor line, `u`/`v` metres off it on the spawn-time basis. */
interface BoltPoint {
  t: number;
  u: number;
  v: number;
}

interface Branch {
  fromIdx: number;
  end: BoltPoint;
}

interface Bolt {
  a: Object3D;
  b: Object3D;
  /** The rig identity this bolt's anchors came from — if the live rig swaps
   *  (LOD tier change) before this bolt dies, it is dropped rather than
   *  sampling bones from a skeleton no longer being drawn. */
  bornRigKey: readonly object[];
  /** Perpendicular basis, built once at spawn from the anchors' direction at
   *  that instant — NOT re-orthogonalised per frame; see file header. */
  basisU: Vector3;
  basisV: Vector3;
  mainPts: readonly BoltPoint[];
  branch: Branch | null;
  age: number;
  life: number;
  /** Per-bolt phase offset so concurrent bolts don't flicker in lockstep. */
  seed: number;
}

const UNIT_X = new Vector3(1, 0, 0);
const UNIT_Y = new Vector3(0, 1, 0);

/** Classic real-time-lightning fractal: split every segment at its midpoint,
 *  displace the split sideways by a shrinking random amount, recurse. */
function midpointDisplace(levels: number, ampStart: number): BoltPoint[] {
  let pts: BoltPoint[] = [{ t: 0, u: 0, v: 0 }, { t: 1, u: 0, v: 0 }];
  let amp = ampStart;
  for (let level = 0; level < levels; level++) {
    const next: BoltPoint[] = [pts[0]!];
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i]!, p1 = pts[i + 1]!;
      next.push({
        t: (p0.t + p1.t) * 0.5,
        u: (p0.u + p1.u) * 0.5 + (Math.random() * 2 - 1) * amp,
        v: (p0.v + p1.v) * 0.5 + (Math.random() * 2 - 1) * amp,
      });
      next.push(p1);
    }
    pts = next;
    amp *= 0.5;
  }
  return pts;
}

/** Lerp two `Color`s into `out` by `t` in [0,1]. Allocation-free per call. */
function lerpColorInto(a: Color, b: Color, t: number, out: Color): Color {
  out.r = a.r + (b.r - a.r) * t;
  out.g = a.g + (b.g - a.g) * t;
  out.b = a.b + (b.b - a.b) * t;
  return out;
}

export function createBoostThunderSystem(): RenderSubsystem {
  let ctxRef: RenderContext | null = null;
  let line: LineSegments | null = null;
  let material: ShaderMaterial | null = null;
  let posAttr: BufferAttribute | null = null;
  let colorAttr: BufferAttribute | null = null;
  let alphaAttr: BufferAttribute | null = null;

  const bolts: Bolt[] = [];
  let spawnTimer = 0;

  /** Same identity-cached rig pattern as boostAura.ts — see its header for
   *  why the cache key must be the silhouette array's IDENTITY, not a copy. */
  let rigKey: readonly object[] | null = null;
  let rig: EmberRig | null = null;

  const core = new Color(BOLT_CORE_COLOR);
  const edge = new Color(BOLT_EDGE_COLOR);
  const scratchColor = new Color();
  const scratchA = new Vector3();
  const scratchB = new Vector3();
  const scratchPos = new Vector3();
  const scratchDir = new Vector3();

  function ensureRig(slot: number): EmberRig | null {
    if (ctxRef === null) return null;
    const src = ctxRef.attachments.silhouette(slot);
    if (src === null || src.length === 0) {
      rigKey = null; rig = null;
      return null;
    }
    if (rigKey === src) return rig;
    const bones = src[0]?.skeleton?.bones ?? [];
    const hands = findHandRig(bones);
    rig = bones.length > 0 ? findEmberRig(bones, hands) : null;
    rigKey = src;
    return rig;
  }

  function boltPos(bolt: Bolt, aPos: Vector3, bPos: Vector3, p: BoltPoint, out: Vector3): Vector3 {
    out.copy(aPos).lerp(bPos, p.t);
    out.addScaledVector(bolt.basisU, p.u);
    out.addScaledVector(bolt.basisV, p.v);
    return out;
  }

  function spawnBolt(): Bolt | null {
    if (rig === null || rigKey === null || rig.points.length < 2) return null;
    const ia = (Math.random() * rig.points.length) | 0;
    let ib = (Math.random() * rig.points.length) | 0;
    if (ib === ia) ib = (ib + 1) % rig.points.length;
    const a = rig.points[ia]!, b = rig.points[ib]!;
    a.updateWorldMatrix(true, false);
    b.updateWorldMatrix(true, false);
    scratchA.setFromMatrixPosition(a.matrixWorld);
    scratchB.setFromMatrixPosition(b.matrixWorld);
    const segLen = scratchA.distanceTo(scratchB);
    if (segLen < 0.05) return null; // anchors coincide — stay safe, skip this spawn

    scratchDir.subVectors(scratchB, scratchA).normalize();
    const ref = Math.abs(scratchDir.y) < 0.9 ? UNIT_Y : UNIT_X;
    const basisU = new Vector3().crossVectors(scratchDir, ref).normalize();
    const basisV = new Vector3().crossVectors(scratchDir, basisU).normalize();

    const mainPts = midpointDisplace(BOLT_LEVELS, segLen * JITTER_FRACTION);

    let branch: Branch | null = null;
    if (Math.random() < BRANCH_CHANCE) {
      const fromIdx = 1 + ((Math.random() * (mainPts.length - 2)) | 0);
      const from = mainPts[fromIdx]!;
      const ang = Math.random() * Math.PI * 2;
      const reach = segLen * (0.15 + Math.random() * 0.2);
      branch = {
        fromIdx,
        end: {
          t: MathUtils.clamp(from.t + (Math.random() * 2 - 1) * 0.12, 0, 1),
          u: from.u + Math.cos(ang) * reach,
          v: from.v + Math.sin(ang) * reach,
        },
      };
    }

    return {
      a, b, bornRigKey: rigKey, basisU, basisV, mainPts, branch,
      age: 0,
      life: BOLT_LIFE_MIN + Math.random() * (BOLT_LIFE_MAX - BOLT_LIFE_MIN),
      seed: Math.random() * 1000,
    };
  }

  return {
    name: 'boostThunder',

    init(ctx: RenderContext): void {
      ctxRef = ctx;
      const geo = new BufferGeometry();
      const position = new Float32Array(MAX_VERTS * 3);
      const color = new Float32Array(MAX_VERTS * 3);
      const alpha = new Float32Array(MAX_VERTS);
      posAttr = new BufferAttribute(position, 3);
      colorAttr = new BufferAttribute(color, 3);
      alphaAttr = new BufferAttribute(alpha, 1);
      posAttr.setUsage(DynamicDrawUsage);
      colorAttr.setUsage(DynamicDrawUsage);
      alphaAttr.setUsage(DynamicDrawUsage);
      geo.setAttribute('position', posAttr);
      geo.setAttribute('aColor', colorAttr);
      geo.setAttribute('aAlpha', alphaAttr);
      geo.setDrawRange(0, 0);

      material = new ShaderMaterial({
        vertexShader: VERTEX_SHADER,
        fragmentShader: FRAGMENT_SHADER,
        transparent: true,
        depthWrite: false,
        // Same reasoning as boostAura.ts's embers: the anchor bones sit
        // inside the character's own mesh volume, so depth-testing against
        // his already-drawn body would hide most of every bolt.
        depthTest: false,
        blending: NormalBlending,
      });

      const ls = new LineSegments(geo, material);
      ls.name = 'boost-thunder-bolts';
      ls.frustumCulled = false; // positions are rewritten in world space every frame
      line = ls;
      ctx.scene.add(ls);
    },

    update(state: RenderState): void {
      const ls = line;
      const pAttr = posAttr, cAttr = colorAttr, aAttr = alphaAttr;
      if (ls === null || pAttr === null || cAttr === null || aAttr === null || ctxRef === null) return;

      // Tier 2 only — an identical crackle on tier 1 would erase the
      // distinction boostAura.ts already worked to draw between the tiers.
      const wantThunder = state.boostAuraTier >= 2;
      const dt = Math.max(state.dt, 0);

      if (wantThunder) {
        const slot = state.boostAuraSlot;
        if (slot >= 0) {
          ensureRig(slot);
          spawnTimer -= dt;
          if (spawnTimer <= 0) {
            if (rig !== null && bolts.length < MAX_BOLTS) {
              const b = spawnBolt();
              if (b) bolts.push(b);
            }
            spawnTimer = BOLT_INTERVAL_MIN + Math.random() * (BOLT_INTERVAL_MAX - BOLT_INTERVAL_MIN);
          }
        }
      } else {
        spawnTimer = 0;
      }

      if (bolts.length === 0) {
        if (ls.geometry.drawRange.count !== 0) ls.geometry.setDrawRange(0, 0);
        return;
      }

      const posArr = pAttr.array as Float32Array;
      const colorArr = cAttr.array as Float32Array;
      const alphaArr = aAttr.array as Float32Array;

      const emit = (p: BoltPoint, bolt: Bolt, cr: number, cg: number, cb: number, a: number, w: number): number => {
        if (w >= MAX_VERTS) return w;
        boltPos(bolt, scratchA, scratchB, p, scratchPos);
        const o3 = w * 3;
        posArr[o3] = scratchPos.x; posArr[o3 + 1] = scratchPos.y; posArr[o3 + 2] = scratchPos.z;
        colorArr[o3] = cr; colorArr[o3 + 1] = cg; colorArr[o3 + 2] = cb;
        alphaArr[w] = a;
        return w + 1;
      };

      let w = 0;
      let write = 0;
      for (let i = 0; i < bolts.length; i++) {
        const bolt = bolts[i]!;
        bolt.age += dt;
        if (bolt.age >= bolt.life || bolt.bornRigKey !== rigKey) continue; // expired, or its rig tier is gone
        bolts[write++] = bolt;

        bolt.a.updateWorldMatrix(true, false);
        bolt.b.updateWorldMatrix(true, false);
        scratchA.setFromMatrixPosition(bolt.a.matrixWorld);
        scratchB.setFromMatrixPosition(bolt.b.matrixWorld);

        const t01 = bolt.age / bolt.life;
        // Fast attack, held-with-flicker, fast decay — a strike, not a fade.
        // The sine "stutter" is the multi-return-stroke cue that sells
        // electricity over a plain brightening/dimming line.
        const attack = MathUtils.smoothstep(t01, 0, 0.08);
        const decay = 1 - MathUtils.smoothstep(t01, 0.55, 1.0);
        const flicker = 0.55 + 0.45 * Math.sin(t01 * 26 + bolt.seed);
        const a = Math.max(0, attack * decay * flicker);

        lerpColorInto(core, edge, Math.min(1, t01 * 1.3), scratchColor);
        const cr = scratchColor.r * GLOW_INTENSITY;
        const cg = scratchColor.g * GLOW_INTENSITY;
        const cb = scratchColor.b * GLOW_INTENSITY;

        for (let k = 0; k < bolt.mainPts.length - 1; k++) {
          if (w + 2 > MAX_VERTS) break;
          w = emit(bolt.mainPts[k]!, bolt, cr, cg, cb, a, w);
          w = emit(bolt.mainPts[k + 1]!, bolt, cr, cg, cb, a, w);
        }
        if (bolt.branch !== null && w + 2 <= MAX_VERTS) {
          w = emit(bolt.mainPts[bolt.branch.fromIdx]!, bolt, cr, cg, cb, a, w);
          w = emit(bolt.branch.end, bolt, cr, cg, cb, a, w);
        }
      }
      bolts.length = write;

      pAttr.needsUpdate = true;
      cAttr.needsUpdate = true;
      aAttr.needsUpdate = true;
      ls.geometry.setDrawRange(0, w);
    },

    dispose(): void {
      bolts.length = 0;
      if (line) ctxRef?.scene.remove(line);
      line?.geometry.dispose();
      material?.dispose();
      line = null;
      material = null;
      posAttr = null;
      colorAttr = null;
      alphaAttr = null;
      ctxRef = null;
    },
  };
}
